/**
 * Boolean Transformation Utilities
 *
 * Provides type-safe utilities for transforming Boolean values between
 * API format ("True"/"False") and display format ("Yes"/"No").
 *
 * Used throughout the application for consistent Boolean handling in:
 * - Form values (checkboxes)
 * - API payloads (requests/responses)
 * - Display values (Summary tab, UI)
 */

import { Attribute } from "../types";

/**
 * Normalize various boolean representations to a consistent boolean value or null
 * Handles: true/false, "true"/"false", "True"/"False", "yes"/"no", "Yes"/"No", 1/0, "1"/"0"
 *
 * @param value - Value to normalize (can be string, boolean, number, null, undefined)
 * @returns boolean | null - true, false, or null if value is empty/invalid
 */
export function normalizeBooleanValue(value: any): boolean | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  // Handle boolean primitives
  if (typeof value === "boolean") {
    return value;
  }

  // Handle string representations
  if (typeof value === "string") {
    const normalized = value.toLowerCase().trim();

    if (normalized === "true" || normalized === "yes" || normalized === "1") {
      return true;
    }

    if (normalized === "false" || normalized === "no" || normalized === "0") {
      return false;
    }
  }

  // Handle numeric representations
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }

  // If we can't determine the value, return null
  return null;
}

/**
 * Type-safe check for truthy Boolean values
 * Handles multiple formats: "True", "true", "Yes", "yes", true, 1, "1"
 *
 * @param value - Value to check
 * @returns boolean - true if value represents a truthy Boolean, false otherwise
 */
export function isTruthyBoolean(value: any): boolean {
  const normalized = normalizeBooleanValue(value);
  return normalized === true;
}

/**
 * Convert Boolean API value to user-friendly display value
 * API format: "True" or "False"
 * Display format: "Yes" or "No"
 *
 * @param value - Boolean value from API or form (can be "True", "False", true, false, null, undefined)
 * @returns string - "Yes", "No", or empty string
 */
export function booleanValueToDisplay(
  value: string | boolean | null | undefined
): string {
  const normalized = normalizeBooleanValue(value);

  if (normalized === null) {
    return "No"; // Default to "No" for empty/null values
  }

  return normalized ? "Yes" : "No";
}

/**
 * Convert user-friendly display value to Boolean API value
 * Display format: "Yes" or "No"
 * API format: "True" or "False"
 *
 * @param displayValue - Display value ("Yes" or "No")
 * @returns string - "True", "False", or empty string
 */
export function displayValueToBoolean(
  displayValue: string | null | undefined
): string {
  if (!displayValue || displayValue === "") {
    return "False"; // Default to "False" for empty values
  }

  const normalized = displayValue.toLowerCase().trim();

  if (normalized === "yes" || normalized === "true" || normalized === "1") {
    return "True";
  }

  return "False";
}

/**
 * Transform a single Boolean attribute for API payload (outgoing request)
 * Sets both `value` (API format) and `displayValue` (display format)
 *
 * @param attribute - Attribute object with formValue
 * @returns Transformed attribute with value and displayValue
 */
export function transformAttributeBooleanForPayload(
  attribute: Partial<Attribute> & { formValue?: string }
): Partial<Attribute> & { formValue?: string } {
  if (attribute.dataType !== "Boolean") {
    return attribute;
  }

  const formValue = attribute.formValue || "";
  const normalized = normalizeBooleanValue(formValue);

  // Set API value ("True"/"False")
  const apiValue = normalized === true ? "True" : "False";

  // Set display value ("Yes"/"No")
  const displayValue = normalized === true ? "Yes" : "No";

  return {
    ...attribute,
    value: apiValue,
    displayValue: displayValue,
    formValue: formValue, // Keep original form value
  };
}

/**
 * Transform a single Boolean attribute from API response (incoming data)
 * Populates displayValue from API value if not already present
 *
 * @param attribute - Attribute object from API response
 * @returns Transformed attribute with displayValue populated
 */
export function transformAttributeBooleanFromResponse(
  attribute: Partial<Attribute>
): Partial<Attribute> {
  if (attribute.dataType !== "Boolean") {
    return attribute;
  }

  // If displayValue is already set, keep it
  if (attribute.displayValue) {
    return attribute;
  }

  // Transform value to displayValue
  const normalized = normalizeBooleanValue(attribute.value);
  const displayValue = normalized === true ? "Yes" : "No";

  return {
    ...attribute,
    displayValue: displayValue,
  };
}

/**
 * Transform all Boolean attributes in an array for API payload
 * Used when preparing data for API requests
 *
 * @param attributes - Array of attributes
 * @returns Transformed array with Boolean attributes having both value and displayValue
 */
export function transformBooleanAttributesForPayload(
  attributes: (Partial<Attribute> & { formValue?: string })[]
): (Partial<Attribute> & { formValue?: string })[] {
  return attributes.map((attr) => {
    if (attr.dataType === "Boolean") {
      return transformAttributeBooleanForPayload(attr);
    }
    return attr;
  });
}

/**
 * Transform all Boolean attributes in an array from API response
 * Used when receiving data from API to populate displayValue
 *
 * @param attributes - Array of attributes from API response
 * @returns Transformed array with Boolean attributes having displayValue
 */
export function transformBooleanAttributesFromResponse(
  attributes: Partial<Attribute>[]
): Partial<Attribute>[] {
  return attributes.map((attr) => {
    if (attr.dataType === "Boolean") {
      return transformAttributeBooleanFromResponse(attr);
    }
    return attr;
  });
}

/**
 * Transform Boolean attributes in nested entity structure from API response
 * Handles the common pattern of entities containing attribute arrays
 *
 * @param entities - Object with entity names as keys and attribute arrays as values
 * @returns Transformed entities with Boolean displayValues populated
 */
export function transformBooleanEntitiesFromResponse(
  entities: Record<string, Partial<Attribute>[]>
): Record<string, Partial<Attribute>[]> {
  const transformed: Record<string, Partial<Attribute>[]> = {};

  for (const [entityName, attributes] of Object.entries(entities)) {
    transformed[entityName] =
      transformBooleanAttributesFromResponse(attributes);
  }

  return transformed;
}

/**
 * Get the display value for a Boolean control from form values
 * Used in components to display Boolean values in user-friendly format
 *
 * @param control - Control/Attribute object
 * @param formValues - Form values object
 * @returns string - Display value ("Yes"/"No") or empty string
 */
export function getBooleanDisplayValue(
  control: Partial<Attribute>,
  formValues: Record<string, string>
): string {
  if (control.dataType !== "Boolean") {
    return "";
  }

  // First check if displayValue is already set on the control
  if (control.displayValue) {
    return control.displayValue;
  }

  // Otherwise, get from form values and transform
  const formValue = formValues[control._id || ""] || "";
  return booleanValueToDisplay(formValue);
}
